import { Search, Calculator, Target, ArrowRight, Compass, LineChart, GitCompare, Briefcase, Share2, Palette, Check } from "lucide-react";
import { SectionHeader, Reveal, FeatureCard } from "./primitives";
import { DealScoreGauge } from "../DealScoreGauge";
import { fmtMoney, fmtSigned, fmtPct, dealProperty } from "./format";

const STEPS = [
  { n: "01", icon: Search, title: "Search a Property", desc: "Enter an address, city, ZIP, or supported listing URL." },
  { n: "02", icon: Calculator, title: "RealixData Runs the Numbers", desc: "Automatically calculate rent, mortgage, expenses, cash flow, cap rate, ROI and other supported metrics." },
  { n: "03", icon: Target, title: "Make a Better Decision", desc: "See the Deal Score, compare opportunities, save properties and use available advanced tools." },
];

export function HowItWorksSection({ onCta }) {
  return (
    <section className="rd-section bg-white border-y border-rd-border" data-testid="how-it-works">
      <div className="rd-container">
        <Reveal><SectionHeader eyebrow="How it works" title="Analyze a Property in Three Steps" align="center" /></Reveal>
        <ol className="mt-12 grid md:grid-cols-3 gap-6 lg:gap-10">
          {STEPS.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 90} className="relative rd-card p-7">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-rd-muted">{s.n}</span>
                <span className="w-10 h-10 rounded-lg bg-rd-green-soft text-rd-green inline-flex items-center justify-center"><s.icon className="w-5 h-5" aria-hidden="true" /></span>
              </div>
              <h3 className="rd-h3 text-xl text-rd-text mt-6">{s.title}</h3>
              <p className="mt-2 text-rd-text-2 leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </ol>
        <div className="mt-10 text-center">
          <button type="button" onClick={onCta} className="rd-btn rd-btn-navy" data-testid="how-cta">
            Analyze a Property <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Compass, title: "Find Deals", description: "Search investment properties across supported markets and discover opportunities ranked by Deal Score." },
  { icon: LineChart, title: "Analyze Deals", description: "Cash flow, cap rate, ROI, expenses, financing assumptions and the Deal Score — calculated for every property." },
  { icon: GitCompare, title: "Compare Deals", description: "Put opportunities side by side and quickly determine which have stronger fundamentals.", badge: "Pro" },
  { icon: Briefcase, title: "Track Investments", description: "Portfolio dashboard and ongoing investment tools for managing multiple properties at scale.", badge: "Elite" },
];

const ADVANCED = [
  { label: "BRRRR Analysis", tier: "Pro" },
  { label: "Flip Analysis", tier: "Pro" },
  { label: "Airbnb Comparison", tier: "Pro" },
  { label: "AI Deal Insights", tier: "Pro" },
  { label: "30-Year Repair Projection", tier: null },
  { label: "PDF / Excel Reports", tier: "Pro" },
  { label: "Bulk Analysis", tier: "Elite" },
  { label: "Custom Deal Scoring", tier: "Elite" },
];

export function FeaturesSection() {
  return (
    <section className="rd-section bg-rd-offwhite" data-testid="features-section">
      <div className="rd-container">
        <Reveal>
          <SectionHeader eyebrow="Everything in one place" title="Built for the Full Investment Workflow" subtitle="From the first search to a managed portfolio — every step uses the same transparent numbers." />
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 70}><FeatureCard {...f} className="h-full" /></Reveal>
          ))}
        </div>
        <Reveal className="mt-8 rd-card p-5 sm:p-6" delay={200}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
            <p className="rd-eyebrow text-rd-text-2 flex-shrink-0">Advanced analysis</p>
            <ul className="flex flex-wrap gap-2" aria-label="Advanced analysis tools">
              {ADVANCED.map((a) => (
                <li key={a.label} className="inline-flex items-center gap-2 min-h-[36px] px-3 rounded-lg border border-rd-border bg-rd-offwhite text-sm font-medium text-rd-text">
                  {a.label}
                  {a.tier && <span className={`text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${a.tier === "Elite" ? "bg-rd-navy text-white" : "bg-rd-green-soft text-rd-green"}`}>{a.tier}</span>}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const REALTOR_BENEFITS = [
  { icon: Calculator, title: "Analyze", desc: "Paste a listing or address — rent, taxes and comparable data are pulled automatically." },
  { icon: Share2, title: "Share", desc: "Send a clean link your clients can open on any device. No spreadsheets attached." },
  { icon: Palette, title: "Brand", desc: "Your name and contact details appear on every report you share." },
];

export function RealtorSection({ deal, onCta }) {
  const p = dealProperty(deal);
  const cf = deal?.cash_flow_monthly;
  return (
    <section className="rd-section bg-white border-y border-rd-border" data-testid="create-report-cta-section">
      <div className="rd-container grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <SectionHeader
            eyebrow="For realtors & agents"
            title="Turn Listings Into Investor-Ready Reports"
            subtitle="Analyze a property and share a professional investment report with clients in seconds."
          />
          <ul className="mt-8 space-y-5">
            {REALTOR_BENEFITS.map((b) => (
              <li key={b.title} className="flex gap-4">
                <span className="w-10 h-10 rounded-lg bg-rd-navy text-white inline-flex items-center justify-center flex-shrink-0"><b.icon className="w-5 h-5" aria-hidden="true" /></span>
                <div>
                  <h3 className="font-bold text-rd-text">{b.title}</h3>
                  <p className="text-sm text-rd-text-2 leading-relaxed">{b.desc}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <button type="button" onClick={onCta} className="rd-btn rd-btn-primary" data-testid="create-report-cta-btn">
              Create Your First Report <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <p className="text-sm text-rd-text-2">Free accounts get 3 reports/day. Pro gets unlimited.</p>
          </div>
        </Reveal>

        <Reveal delay={120} className="relative">
          <div className="absolute -inset-4 rounded-3xl bg-rd-green-soft -z-10" aria-hidden="true" />
          <div className="rd-card overflow-hidden shadow-[0_24px_60px_-30px_rgba(11,18,32,0.35)]" aria-label="Example investment report preview" data-testid="report-preview">
            <div className="flex items-center justify-between px-5 py-3 bg-rd-navy text-white">
              <span className="flex items-center gap-2 text-sm font-bold"><img src="/logo.png" alt="" className="h-5 w-auto" aria-hidden="true" /> Investment Report</span>
              <span className="text-xs text-white/60">Prepared by your agent</span>
            </div>
            {deal ? (
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-bold text-rd-text truncate">{p.address}</p>
                    <p className="text-sm text-rd-text-2">{[p.city, p.state].filter(Boolean).join(", ")}</p>
                    <p className="mt-2 font-mono font-bold text-2xl text-rd-text">{fmtMoney(p.price)}</p>
                  </div>
                  <DealScoreGauge score={deal.deal_score} size="md" />
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3">
                  {[
                    ["Rent", fmtMoney(deal.estimated_monthly_rent), "neutral"],
                    ["Cash Flow", fmtSigned(cf), cf >= 0 ? "positive" : "negative"],
                    ["Cap Rate", fmtPct(deal.cap_rate), "neutral"],
                  ].map(([l, v, t]) => (
                    <div key={l} className="rounded-lg bg-rd-offwhite border border-rd-border p-3">
                      <p className="text-[11px] uppercase tracking-wider text-rd-text-2 font-semibold">{l}</p>
                      <p className={`font-mono font-bold ${t === "positive" ? "text-rd-green" : t === "negative" ? "text-rd-negative" : "text-rd-text"}`}>{v}</p>
                    </div>
                  ))}
                </div>
                <ul className="mt-5 space-y-2 text-sm text-rd-text-2">
                  {["Mortgage & expense breakdown", "Deal Score methodology", "Agent branding & contact"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-rd-green" aria-hidden="true" /> {x}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="p-6 space-y-3" aria-hidden="true">
                <div className="rd-skeleton h-5 w-2/3" /><div className="rd-skeleton h-4 w-1/3" /><div className="rd-skeleton h-24 w-full" />
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
